import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendOtp, verifyOtp } from '../services/authService';
import { getDashboardRoute } from '../services/api';

const IconSpinner = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    width="16" height="16" className="spin">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

const LeftPanel = () => (
  <div className="left-panel">
    {[
      { top: '-90px',  right: '-90px', size: '380px', color: 'rgba(167,139,250,0.3)',  anim: 'orb1 9s ease-in-out infinite' },
      { bottom: '-70px', left: '-70px', size: '320px', color: 'rgba(99,102,241,0.35)', anim: 'orb2 11s ease-in-out infinite' },
      { top: '42%',  left: '18%',      size: '200px', color: 'rgba(196,181,253,0.18)', anim: 'orb1 6s ease-in-out infinite reverse' },
    ].map((o, i) => (
      <div key={i} style={{
        position: 'absolute', top: o.top, right: o.right, bottom: o.bottom, left: o.left,
        width: o.size, height: o.size, borderRadius: '50%',
        background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
        animation: o.anim,
      }}/>
    ))}
    <div className="left-panel-dot-grid" />

    <div className="left-panel-logo">
      <div className="left-panel-logo-inner">
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
        <span className="panel-logo-text">ArogyaOne</span>
      </div>
    </div>

    <div className="left-panel-content">
      <div className="panel-badge-row">
        {[
          { top: '0px',  left: '0px',   label: 'Super Admin',  delay: '0s'   },
          { top: '0px',  left: '136px', label: 'Doctor',       delay: '0.2s' },
          { top: '36px', left: '20px',  label: 'Tenant Admin', delay: '0.4s' },
          { top: '36px', left: '152px', label: 'Staff',        delay: '0.6s' },
        ].map(b => (
          <div key={b.label} className="left-panel-badge"
            style={{ top: b.top, left: b.left, animation: `floatBadge 4s ease-in-out ${b.delay} infinite` }}>
            {b.label}
          </div>
        ))}
      </div>
      <h2 className="panel-headline">
        One login.<br />
        <span className="panel-gradient-text">Every role.</span>
      </h2>
      <p className="panel-sub">Healthcare Management Platform</p>
    </div>

    <div className="left-panel-stats">
      {[['6', 'Role types'], ['Multi', 'Tenant'], ['RBAC', 'Secured']].map(([val, lbl]) => (
        <div key={lbl}>
          <div className="panel-stat-val">{val}</div>
          <div className="panel-stat-lbl">{lbl}</div>
        </div>
      ))}
    </div>
  </div>
);

const ErrorBox = ({ message }) => (
  <div className="error-box">
    <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" width="15" height="15" className="flex-shrink-0">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    <span className="error-text">{message}</span>
  </div>
);

const Login = () => {
  const navigate           = useNavigate();
  const location          = useLocation();
  const { loginWithToken } = useAuth();
  const redirectTo         = location.state?.from || null;

  const [phone,   setPhone]   = useState('');
  const [otp,     setOtp]     = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [role,    setRole]    = useState('PATIENT');

  const triggerShake  = () => { setShaking(true); setTimeout(() => setShaking(false), 500); };
  const validatePhone = (p) => /^[6-9]\d{9}$/.test(p);

  const handleSendOtp = async () => {
    if (!validatePhone(phone)) { setError('Enter a valid 10-digit mobile number'); triggerShake(); return; }
    setLoading(true); setError('');
    try { await sendOtp(phone, role); setShowOtp(true); }
    catch (err) { setError(err?.message || err || 'Failed to send OTP. Try again.'); triggerShake(); }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { setError('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      const result = await verifyOtp(phone, role, otp);
      loginWithToken(result);
      setSuccess(true);
      setTimeout(() => {
        // First-time login → complete profile before anything else
        if (!result.user.profileCompleted) {
          navigate('/profile', { replace: true, state: { firstLogin: true } });
        } else if (redirectTo) {
          navigate(redirectTo, { replace: true });
        } else {
          navigate(getDashboardRoute(result.user.role));
        }
      }, 900);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Verification failed');
      triggerShake();
    }
    setLoading(false);
  };

  const handleResend = () => { setShowOtp(false); setOtp(''); setError(''); };

  return (
    <div className="login-page">
      <div className="login-left"><LeftPanel /></div>

      <div className="login-right">
        <div className="login-wrap">

          <div className="login-mobile-logo">
            <div className="login-mobile-logo-inner">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              <span className="login-logo-text">ArogyaOne</span>
            </div>
            <p className="login-logo-sub">Healthcare Management Platform</p>
          </div>

          <div className={`card-elevated form-card${shaking ? ' shake' : ''}`}>

            <div className="login-card-head">
              <div className="icon-box">
                <svg viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
              </div>
              <h1 className="login-title">{showOtp ? 'Enter OTP' : 'Welcome back'}</h1>
              <p className="login-sub">
                {showOtp ? `We sent a 6-digit code to +91 ${phone}` : 'Sign in with your mobile number'}
              </p>
            </div>

            {success && (
              <div className="success-state">
                <div className="success-circle">
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <p className="success-title">Signed in successfully!</p>
                <p className="t-meta m-0">Redirecting…</p>
              </div>
            )}

            {!success && (
              <>
                {!showOtp && (
                  <div>
                    <div className="field">
                      <label className="t-label">Mobile number</label>
                      <div className="phone-row">
                        <div className="phone-prefix">🇮🇳 +91</div>
                        <input className={`input-lg${error ? ' err' : ''}`}
                          type="tel" inputMode="numeric" maxLength={10} value={phone}
                          onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setError(''); }}
                          placeholder="Enter 10-digit number"
                          onKeyDown={e => e.key === 'Enter' && handleSendOtp()} autoFocus />
                      </div>
                    </div>
                    <div className="field">
                      <label className="t-label">I am a</label>
                      <select className={`input-lg${error ? ' err' : ''}`} value={role} onChange={e => setRole(e.target.value)}>
                        <option value="PATIENT">Patient</option>
                        <option value="DOCTOR">Doctor</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </div>
                    {error && <ErrorBox message={error} />}
                    <button type="button" onClick={handleSendOtp} disabled={loading} className="btn-primary">
                      {loading ? <><IconSpinner /> Sending OTP…</> : 'Send OTP →'}
                    </button>
                  </div>
                )}

                {showOtp && (
                  <div>
                    <div className="field">
                      <div className="otp-label-row">
                        <label className="t-label-inline">6-digit OTP</label>
                        <button type="button" onClick={handleResend} className="t-link t-link-xs">← Change number</button>
                      </div>
                      <input className={`input-lg otp-input${error ? ' err' : ''}`}
                        type="text" inputMode="numeric" maxLength={6} value={otp}
                        onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                        placeholder="• • • • • •"
                        onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()} autoFocus />
                    </div>
                    {error && <ErrorBox message={error} />}
                    <button type="button" onClick={handleVerifyOtp} disabled={loading} className="btn-primary">
                      {loading ? <><IconSpinner /> Verifying…</> : 'Verify & Sign in'}
                    </button>
                    <p className="t-meta resend-row">
                      Didn't receive it?{' '}
                      <button type="button" className="t-link t-link-sm2"
                        onClick={async () => { setOtp(''); setError(''); setLoading(true); await sendOtp(phone, role); setLoading(false); }}>
                        Resend OTP
                      </button>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;